import { FilterOutlined, ReloadOutlined, SettingOutlined } from "@ant-design/icons";
import { Button, Card, Checkbox, Col, Divider, Flex, Popover, Row, Typography } from "antd";
import { useState } from "react";
import { border, radius, shadow, spacing, typography } from "../../theme/tokens";

const { Text } = Typography;

const DEFAULT_FILTER_SPAN = { xs: 24, sm: 12, md: 6 };

/**
 * State dùng chung cho các trang danh sách:
 *  - visibleFilters:  danh sách key bộ lọc đang hiển thị
 *  - visibleColumns:  danh sách key cột đang hiển thị
 *
 * @param {object} opts
 * @param {string[]} opts.defaultVisibleFilters
 * @param {string[]} opts.defaultVisibleColumns
 */
export function useListToolbar({ defaultVisibleFilters = [], defaultVisibleColumns = [] } = {}) {
    const [visibleFilters, setVisibleFilters] = useState(defaultVisibleFilters);
    const [visibleColumns, setVisibleColumns] = useState(defaultVisibleColumns);
    return { visibleFilters, setVisibleFilters, visibleColumns, setVisibleColumns };
}

/**
 * Lọc ra danh sách cột antd theo cột đang hiển thị.
 * Cột có `fixed: true` luôn hiển thị (STT, thao tác...).
 *
 * @param {Array<{key:string,fixed?:boolean,column:object}>} columnDefs
 * @param {string[]} visibleColumns
 * @returns {object[]} mảng column config cho <Table>
 */
export function selectVisibleColumns(columnDefs, visibleColumns) {
    return columnDefs
        .filter((d) => d.fixed || visibleColumns.includes(d.key))
        .map((d) => d.column)
        .filter(Boolean);
}

/**
 * Thanh công cụ dùng chung cho trang danh sách (chuẩn "Lịch sử gửi tin"):
 *  - Nút "Bộ lọc": popover bật/tắt các trường lọc.
 *  - Nút "Cột hiển thị": popover bật/tắt cột.
 *  - "Xóa lọc" + slot `extra` (Thêm mới / Xuất Excel...) + "Làm mới".
 *  - Khu vực trường lọc tự render theo `filterDefs` đang bật.
 *
 * @param {Array<{key,label,span?,field?}>} filterDefs  Định nghĩa bộ lọc.
 * @param {Array<{key,label,fixed?}>} columnDefs         Định nghĩa cột (để chọn ẩn/hiện).
 */
export default function ListToolbar({
    filterDefs = [],
    visibleFilters = [],
    onChangeVisibleFilters,
    columnDefs = [],
    visibleColumns = [],
    onChangeVisibleColumns,
    defaultVisibleColumns = [],
    hasActiveFilter = false,
    onClearFilter,
    onReload,
    extra,
    style,
}) {
    const toggleableColumns = columnDefs.filter((c) => !c.fixed);
    const activeFilterDefs = filterDefs.filter((f) => visibleFilters.includes(f.key));

    const filterToggleContent = (
        <div style={{ width: 220 }}>
            <div style={{ padding: `${spacing[2]}px ${spacing[3]}px`, borderBottom: border.divider }}>
                <Text strong style={{ fontSize: typography.size.sm }}>Chọn bộ lọc hiển thị</Text>
            </div>
            <div style={{ padding: spacing[2], maxHeight: 320, overflowY: "auto" }}>
                {filterDefs.map((f) => (
                    <div key={f.key} style={{ padding: `${spacing[1]}px 0` }}>
                        <Checkbox
                            checked={visibleFilters.includes(f.key)}
                            onChange={(e) =>
                                onChangeVisibleFilters?.(
                                    e.target.checked
                                        ? [...visibleFilters, f.key]
                                        : visibleFilters.filter((k) => k !== f.key)
                                )
                            }
                        >
                            {f.label}
                        </Checkbox>
                    </div>
                ))}
            </div>
        </div>
    );

    const columnPickerContent = (
        <div style={{ width: 240 }}>
            <div style={{ padding: `${spacing[2]}px ${spacing[3]}px`, borderBottom: border.divider }}>
                <Text strong style={{ fontSize: typography.size.sm }}>Chọn cột hiển thị</Text>
            </div>
            <div style={{ padding: spacing[2], maxHeight: 320, overflowY: "auto" }}>
                {toggleableColumns.map((col) => (
                    <div key={col.key} style={{ padding: `${spacing[1]}px 0` }}>
                        <Checkbox
                            checked={visibleColumns.includes(col.key)}
                            onChange={(e) =>
                                onChangeVisibleColumns?.(
                                    e.target.checked
                                        ? [...visibleColumns, col.key]
                                        : visibleColumns.filter((k) => k !== col.key)
                                )
                            }
                        >
                            {col.label}
                        </Checkbox>
                    </div>
                ))}
            </div>
            <div style={{ padding: `${spacing[2]}px ${spacing[3]}px`, borderTop: border.divider }}>
                <Button
                    type="link" size="small"
                    onClick={() => onChangeVisibleColumns?.(defaultVisibleColumns)}
                    style={{ padding: 0, fontSize: typography.size.sm }}
                >
                    Đặt lại mặc định
                </Button>
            </div>
        </div>
    );

    return (
        <Card
            size="small"
            style={{ marginBottom: spacing[4], borderRadius: radius.md, border: border.hairline, boxShadow: shadow.xs, ...style }}
            styles={{ body: { padding: spacing[4] } }}
        >
            <Flex justify="space-between" align="center" wrap="wrap" gap={spacing[3]}>
                <Flex gap={spacing[2]} align="center" wrap="wrap">
                    {filterDefs.length > 0 && (
                        <Popover content={filterToggleContent} trigger="click" placement="bottomLeft">
                            <Button icon={<FilterOutlined />}>
                                Bộ lọc {visibleFilters.length > 0 && `(${visibleFilters.length})`}
                            </Button>
                        </Popover>
                    )}
                    {toggleableColumns.length > 0 && (
                        <Popover content={columnPickerContent} trigger="click" placement="bottomLeft">
                            <Button icon={<SettingOutlined />}>Cột hiển thị</Button>
                        </Popover>
                    )}
                    {hasActiveFilter && onClearFilter && (
                        <Button type="link" size="small" danger onClick={onClearFilter} style={{ fontSize: typography.size.sm }}>
                            Xóa lọc
                        </Button>
                    )}
                </Flex>
                {(extra || onReload) && (
                    <Flex gap={spacing[2]} align="center" wrap="wrap">
                        {extra}
                        {onReload && (
                            <Button icon={<ReloadOutlined />} onClick={onReload}>Làm mới</Button>
                        )}
                    </Flex>
                )}
            </Flex>

            {activeFilterDefs.length > 0 && (
                <>
                    <Divider style={{ margin: `${spacing[3]}px 0` }} />
                    <Row gutter={[spacing[3], spacing[3]]}>
                        {activeFilterDefs.map((f) => (
                            <Col key={f.key} {...(f.span || DEFAULT_FILTER_SPAN)}>
                                {f.field}
                            </Col>
                        ))}
                    </Row>
                </>
            )}
        </Card>
    );
}
